/**
 * AppSync Events WebSocket 共通接続フック
 * 
 * 単一のWebSocket接続を管理し、複数のチャンネルをサブスクライブできる。
 * 各チャンネルからのメッセージを適切なコールバックにルーティングする。
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { getQuestionBackendConfig } from '../api/questionBackend';
import { ensureFrontendSettings } from '../api/frontendSettings';
import { MAX_BACKOFF_EXPONENT, backoffDelayMs, DEFAULT_KA_TIMEOUT_MS } from '../utils/wsBackoff';

function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// =============================================================================
// 型定義
// =============================================================================

/** チャンネルサブスクリプション */
export interface ChannelSubscription {
  channel: string;
  onMessage: (event: unknown) => void;
  onSubscribeSuccess?: () => void;
  onSubscribeError?: (error: unknown) => void;
}

export interface UseAppSyncConnectionOptions {
  tenantId: string;
  lectureId: string;
  enabled?: boolean;
  userId?: string; // ユーザーID（オプション）
  onError?: ((error: Error) => void) | undefined;
}

export interface UseAppSyncConnectionReturn {
  isConnected: boolean;
  error: string | null;
  subscribe: (subscription: ChannelSubscription) => string;
  unsubscribe: (subscriptionId: string) => void;
  connect: () => void;
  disconnect: () => void;
}

// =============================================================================
// フック本体
// =============================================================================

export function useAppSyncConnection(
  options: UseAppSyncConnectionOptions
): UseAppSyncConnectionReturn {
  const { tenantId, lectureId, enabled = true, userId, onError } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const isMountedRef = useRef(true);
  const isConnectingRef = useRef(false);
  // keep-alive（ka）途絶検知用（STU-H3対応）
  const kaWatchdogTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const kaTimeoutMsRef = useRef(DEFAULT_KA_TIMEOUT_MS);
  
  // isConnectedのRefバージョン（useCallback内で使用）
  const isConnectedRef = useRef(false);
  
  // サブスクリプションが成功したかを追跡
  const hasSubscribedRef = useRef(false);

  // チャンネルサブスクリプションを管理
  const subscriptionsRef = useRef<Map<string, ChannelSubscription>>(new Map());
  const channelToSubscriptionIdRef = useRef<Map<string, string>>(new Map());

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // enabledのRefバージョン。connectはuseCallbackの依存配列にenabledを含めないため、
  // onclose内でクロージャの古いenabled値（初回レンダリング時のfalse等）を参照すると
  // 再接続がスケジュールされなくなる。常に最新値を参照するためRefを経由する
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  // ------------------------------------------------------------------
  // チャンネルサブスクリプション管理
  // ------------------------------------------------------------------
  const subscribe = useCallback(
    (subscription: ChannelSubscription): string => {
      const subscriptionId = generateUUID();
      subscriptionsRef.current.set(subscriptionId, subscription);
      channelToSubscriptionIdRef.current.set(subscription.channel, subscriptionId);

      console.log('[AppSyncConnection] Subscription registered:', {
        subscriptionId,
        channel: subscription.channel,
      });

      // 既に接続済みの場合は即座にサブスクライブ
      // React Strict Modeでの二重実行を防ぐため、isConnectedRefとreadyStateの両方をチェック
      if (wsRef.current?.readyState === WebSocket.OPEN && isConnectedRef.current) {
        const config = getQuestionBackendConfig();
        const subscribePayload = {
          type: 'subscribe',
          id: subscriptionId,
          channel: subscription.channel,
          authorization: {
            Authorization: config.apiKey,
            host: config.httpEndpoint,
          },
        };
        console.log('[AppSyncConnection] Subscribing to channel (immediate):', subscription.channel);
        wsRef.current.send(JSON.stringify(subscribePayload));
      }

      return subscriptionId;
    },
    []
  );

  const unsubscribe = useCallback((subscriptionId: string) => {
    const subscription = subscriptionsRef.current.get(subscriptionId);
    if (subscription) {
      channelToSubscriptionIdRef.current.delete(subscription.channel);
      subscriptionsRef.current.delete(subscriptionId);
      console.log('[AppSyncConnection] Subscription removed:', subscriptionId);

      // WebSocketが接続中の場合はunsubscribeメッセージを送信
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(
          JSON.stringify({
            type: 'unsubscribe',
            id: subscriptionId,
          })
        );
      }
    }
  }, []);

  // ------------------------------------------------------------------
  // サブスクライブメッセージ送信
  // ------------------------------------------------------------------
  const sendSubscribeMessage = useCallback(
    (subscription: ChannelSubscription, subscriptionId: string) => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        console.warn('[AppSyncConnection] WebSocket is not open');
        return;
      }

      const config = getQuestionBackendConfig();
      const subscribePayload = {
        type: 'subscribe',
        id: subscriptionId,
        channel: subscription.channel,
        authorization: {
          Authorization: config.apiKey,
          host: config.httpEndpoint,
        },
      };

      console.log('[AppSyncConnection] Subscribing to channel:', subscription.channel);
      wsRef.current.send(JSON.stringify(subscribePayload));
    },
    []
  );

  // ------------------------------------------------------------------
  // WebSocket 接続
  // ------------------------------------------------------------------
  const connect = useCallback(async () => {
    if (isConnectingRef.current || wsRef.current?.readyState === WebSocket.OPEN) {
      console.log('[AppSyncConnection] Already connected or connecting');
      return;
    }

    // userIdが空または無効な場合は接続しない（認証完了まで待つ）
    if (!userId || userId === 'anonymous' || userId.trim() === '') {
      console.warn('[AppSyncConnection] userId is invalid or empty, skipping connection:', userId);
      return;
    }

    isConnectingRef.current = true;

    try {
      // 設定を取得（frontend-settings API）
      await ensureFrontendSettings();

      // 取得待ちの間にアンマウント・接続が進んでいたら中断する
      // （放置すると誰も参照しない WebSocket が生成されてリークする。受講生B-6対応）
      if (
        !isMountedRef.current ||
        wsRef.current?.readyState === WebSocket.OPEN ||
        wsRef.current?.readyState === WebSocket.CONNECTING
      ) {
        isConnectingRef.current = false;
        return;
      }

      const config = getQuestionBackendConfig();

      if (!config.realtimeEndpoint || !config.httpEndpoint || !config.apiKey) {
        throw new Error('AppSync設定が不足しています');
      }

      // 認証情報をbase64エンコード
      const authorization = {
        host: config.httpEndpoint,
        Authorization: config.apiKey,
        userType: 'student',                          // 受講生固定
        userId,                                       // ユーザーID（'anonymous'フォールバック削除）
      };

      const authHeader = btoa(JSON.stringify(authorization))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');

      console.log('[AppSyncConnection] Connecting to WebSocket...');

      // 破棄しきれていない古いソケット（CLOSING中など）が残っていれば完全に破棄する。
      // 古いソケットのoncloseが新しい接続のwsRefをnull化して再接続を誘発すると、
      // 購読済みソケットが参照を失ったまま残り（ゾンビ接続）イベントが多重受信される
      if (wsRef.current) {
        const staleWs = wsRef.current;
        staleWs.onopen = null;
        staleWs.onmessage = null;
        staleWs.onerror = null;
        staleWs.onclose = null;
        if (staleWs.readyState === WebSocket.CONNECTING || staleWs.readyState === WebSocket.OPEN) {
          staleWs.close();
        }
        wsRef.current = null;
      }

      const ws = new WebSocket(config.realtimeEndpoint, [
        'aws-appsync-event-ws',
        `header-${authHeader}`,
      ]);
      wsRef.current = ws;

      // ------------------------------------------------------------------
      // keep-alive（ka）途絶の検知（STU-H3対応）
      // モバイル回線等では TCP 接続が無音のまま死に、onclose も offline イベントも
      // 発火しない「ゾンビ接続」が発生する。AppSync は接続維持中 ka を定期送信するため、
      // connectionTimeoutMs の間なにも受信しなければ接続が死んだと判断し、
      // 死んだソケットを即座に破棄して再接続する（close 完了の onclose は待たない）
      // ------------------------------------------------------------------
      const clearKaWatchdog = () => {
        if (kaWatchdogTimerRef.current) {
          clearTimeout(kaWatchdogTimerRef.current);
          kaWatchdogTimerRef.current = null;
        }
      };
      const resetKaWatchdog = () => {
        clearKaWatchdog();
        kaWatchdogTimerRef.current = setTimeout(() => {
          if (wsRef.current !== ws) return;
          console.warn('[AppSyncConnection] keep-alive途絶を検知（ゾンビ接続）、再接続します');
          setIsConnected(false);
          isConnectedRef.current = false;
          hasSubscribedRef.current = false;
          ws.onopen = null;
          ws.onmessage = null;
          ws.onerror = null;
          ws.onclose = null;
          wsRef.current = null;
          try {
            ws.close(4001, 'Keep-alive timeout');
          } catch {
            // すでに閉じている場合は無視
          }
          if (isMountedRef.current && enabledRef.current) {
            connect();
          }
        }, kaTimeoutMsRef.current);
      };

      ws.onopen = () => {
        // 既に新しいソケットに置き換えられている場合は自分を閉じて終了（ゾンビ接続防止）
        if (wsRef.current !== ws) {
          ws.close();
          return;
        }
        console.log('[AppSyncConnection] WebSocket connected');
        setIsConnected(true);
        isConnectedRef.current = true;  // Ref更新
        setError(null);
        reconnectAttemptsRef.current = 0;
        isConnectingRef.current = false;

        console.log('[AppSyncConnection] Sending connection_init');
        ws.send(JSON.stringify({ type: 'connection_init' }));
      };

      ws.onmessage = (event) => {
        // 置き換え済みの古いソケットからの受信は無視（イベント多重受信防止）
        if (wsRef.current !== ws) return;
        // 受信（ka含む）があった＝接続は生きているため、途絶検知タイマーをリセット
        resetKaWatchdog();
        try {
          const data = JSON.parse(event.data);
          console.log('[AppSyncConnection] Message received:', data);

          if (data.type === 'connection_ack') {
            console.log('[AppSyncConnection] Connection acknowledged');
            // AppSync が通知するアイドルタイムアウトを ka 途絶検知のしきい値に使う
            if (typeof data.connectionTimeoutMs === 'number' && data.connectionTimeoutMs > 0) {
              kaTimeoutMsRef.current = data.connectionTimeoutMs;
              resetKaWatchdog();
            }

            // 登録済みのすべてのサブスクリプションを送信
            subscriptionsRef.current.forEach((subscription, subscriptionId) => {
              sendSubscribeMessage(subscription, subscriptionId);
            });
          } else if (data.type === 'subscribe_success') {
            console.log('[AppSyncConnection] Subscription successful:', data.id);
            hasSubscribedRef.current = true; // サブスクリプション成功をマーク
            const subscription = subscriptionsRef.current.get(data.id);
            subscription?.onSubscribeSuccess?.();
          } else if (data.type === 'subscribe_error') {
            console.error('[AppSyncConnection] Subscribe error:', data);
            const subscription = subscriptionsRef.current.get(data.id);
            subscription?.onSubscribeError?.(data);
            setError(`サブスクライブエラー: ${data.errors?.[0]?.message || '不明なエラー'}`);
          } else if (data.type === 'connection_error') {
            console.error('[AppSyncConnection] Connection error:', data);
            console.error('[AppSyncConnection] Error details:', JSON.stringify(data.errors, null, 2));
            setError(`接続エラー: ${data.errors?.[0]?.message || '不明なエラー'}`);
          } else if (data.type === 'data' && data.event) {
            // イベントデータのパース
            let eventData: unknown;

            if (typeof data.event === 'string') {
              try {
                const firstParse = JSON.parse(data.event);
                eventData = typeof firstParse === 'string' ? JSON.parse(firstParse) : firstParse;
              } catch {
                console.warn('[AppSyncConnection] Failed to parse event as JSON:', data.event);
                return;
              }
            } else {
              eventData = data.event;
            }

            // サブスクリプションIDからチャンネルを探してコールバックを呼び出す
            const subscription = subscriptionsRef.current.get(data.id);
            if (subscription) {
              subscription.onMessage(eventData);
            } else {
              console.warn('[AppSyncConnection] No subscription found for id:', data.id);
            }
          } else if (data.type === 'error') {
            console.error('[AppSyncConnection] Error from server:', data);
            setError(data.errors?.[0]?.message || data.message || 'サーバーエラーが発生しました');
          } else if (data.type === 'ka') {
            // Keep-alive メッセージは無視
          }
        } catch (err) {
          console.error('[AppSyncConnection] Failed to parse message:', err);
        }
      };

      ws.onerror = (event) => {
        // 置き換え済みの古いソケットのイベントは無視
        if (wsRef.current !== ws) return;
        console.error('[AppSyncConnection] WebSocket error:', event);
        setError('WebSocket接続エラーが発生しました');
        setIsConnected(false);
        isConnectedRef.current = false;  // Ref更新
        isConnectingRef.current = false;
        onErrorRef.current?.(new Error('WebSocket error'));
      };

      ws.onclose = (event) => {
        // 置き換え済みの古いソケットのoncloseは無視する。
        // ここでwsRefをnull化・再接続すると、生きている新しいソケットが
        // 参照を失ったまま残り（ゾンビ接続）イベントが多重受信される
        if (wsRef.current !== ws) return;
        clearKaWatchdog();
        console.log('[AppSyncConnection] WebSocket closed', event.code, event.reason);
        setIsConnected(false);
        isConnectedRef.current = false;  // Ref更新
        hasSubscribedRef.current = false; // サブスクリプション状態をリセット
        wsRef.current = null;
        isConnectingRef.current = false;

        // 無期限に再接続を試行する（指数バックオフ + ジッタ、上限30秒。STU-H2対応）
        // 試行回数での打ち切りは行わない（長時間切断後もリロードなしで復帰させるため）
        // code=1000（正常クローズ）も再接続対象とする（STU-M1対応）。
        // AppSync Events は最大接続時間超過時などサーバー都合でも code=1000 で切断するため、
        // 除外すると数時間視聴し続けた受講生が無通知でリアルタイム更新を失う。
        // アプリ都合の切断（disconnect()）は wsRef を先に null 化しており、
        // 冒頭のステイルガードで再接続はスキップされる
        if (isMountedRef.current && enabledRef.current) {
          reconnectAttemptsRef.current = Math.min(reconnectAttemptsRef.current + 1, MAX_BACKOFF_EXPONENT);
          const delay = backoffDelayMs(reconnectAttemptsRef.current);
          console.log(
            `[AppSyncConnection] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`
          );

          reconnectTimerRef.current = setTimeout(() => {
            if (isMountedRef.current && enabledRef.current) {
              connect();
            }
          }, delay);
        }
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'WebSocket接続に失敗しました';
      console.error('[AppSyncConnection] Connection error:', err);
      setError(errorMessage);
      isConnectingRef.current = false;
      onErrorRef.current?.(err instanceof Error ? err : new Error(errorMessage));

      // WebSocket生成前の失敗（オフライン中の設定取得失敗など）はoncloseが
      // 発火しないため、ここで再試行をスケジュールしないと再接続ループが停止する
      if (isMountedRef.current && enabledRef.current && !wsRef.current) {
        reconnectAttemptsRef.current = Math.min(reconnectAttemptsRef.current + 1, MAX_BACKOFF_EXPONENT);
        const delay = backoffDelayMs(reconnectAttemptsRef.current);
        console.log(
          `[AppSyncConnection] Retrying connect in ${delay}ms (attempt ${reconnectAttemptsRef.current})`
        );
        reconnectTimerRef.current = setTimeout(() => {
          if (isMountedRef.current && enabledRef.current) {
            connect();
          }
        }, delay);
      }
    }
    // enabledはRef経由で参照するため依存配列から除外（無限ループを防ぐため）
  }, [sendSubscribeMessage, userId]);

  // ------------------------------------------------------------------
  // WebSocket 切断
  // ------------------------------------------------------------------
  const disconnect = useCallback(() => {
    console.log('[AppSyncConnection] Disconnecting...');

    isConnectingRef.current = false;

    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (kaWatchdogTimerRef.current) {
      clearTimeout(kaWatchdogTimerRef.current);
      kaWatchdogTimerRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close(1000, 'User initiated disconnect');
      wsRef.current = null;
    }

    setIsConnected(false);
    isConnectedRef.current = false;  // Ref更新
    hasSubscribedRef.current = false; // サブスクリプション状態をリセット
    reconnectAttemptsRef.current = 0;
  }, []);

  // ------------------------------------------------------------------
  // ライフサイクル
  // ------------------------------------------------------------------
  useEffect(() => {
    isMountedRef.current = true;

    console.log('[AppSyncConnection][DEBUG] useEffect実行:', {
      enabled,
      tenantId,
      lectureId,
      userId,
      shouldConnect: enabled && !!tenantId && !!lectureId && !!userId && userId !== 'anonymous',
    });

    // userIdが有効な場合のみ接続を試みる（空文字、'anonymous'、空白文字のみの場合は接続しない）
    if (enabled && tenantId && lectureId && userId && userId !== 'anonymous' && userId.trim() !== '') {
      console.log('[AppSyncConnection] Mounting, initiating connection...');
      const timer = setTimeout(() => {
        if (isMountedRef.current && enabled && userId && userId !== 'anonymous' && userId.trim() !== '') {
          connect();
        }
      }, 100);

      return () => {
        console.log('[AppSyncConnection] Unmounting, cleaning up...');
        clearTimeout(timer);
        isMountedRef.current = false;
        disconnect();
      };
    } else {
      console.log('[AppSyncConnection][DEBUG] 接続条件を満たさない、接続しない');
    }

    return () => {
      isMountedRef.current = false;
    };
    // connect/disconnectは依存配列に含めない（無限ループを防ぐため）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, tenantId, lectureId, userId]);

  // ------------------------------------------------------------------
  // ネットワーク復帰・タブ復帰時の即時再接続
  // （モバイルはバックグラウンドで OS が WebSocket を切断するため、
  //   バックオフ待ちをスキップして即座に接続を回復させる）
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!enabled) return;

    const reconnectNow = () => {
      if (!isMountedRef.current) return;
      if (isConnectingRef.current || wsRef.current?.readyState === WebSocket.OPEN) return;
      // 待機中のバックオフタイマーを破棄して即時接続
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      reconnectAttemptsRef.current = 0;
      console.log('[AppSyncConnection] ネットワーク/タブ復帰を検知、即時再接続');
      connect();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') reconnectNow();
    };

    // ネットワーク切断の即時検知（offlineイベント）
    // Wi-Fiオフ等ではOSがTCP切断を通知せず、oncloseがTCPタイムアウトまで
    // 数分間発火しない（ゾンビ接続）ことがあるため、明示的に切断扱いにする。
    // 「ネットワーク都合の切断」であることをログで区別できるよう4000を使う
    const onOffline = () => {
      if (!isMountedRef.current) return;
      if (!wsRef.current && !isConnectedRef.current) return;
      console.log('[AppSyncConnection] ネットワーク切断を検知（offlineイベント）');
      setIsConnected(false);
      isConnectedRef.current = false;
      wsRef.current?.close(4000, 'Network offline');
    };

    window.addEventListener('online', reconnectNow);
    window.addEventListener('offline', onOffline);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('online', reconnectNow);
      window.removeEventListener('offline', onOffline);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, connect]);

  // ------------------------------------------------------------------
  // Heartbeat 送信は useRealtimeQuestion が独自に実施
  // lecture-status チャンネルはログイン数カウント対象外のため、ここでは送信しない
  // ------------------------------------------------------------------

  return {
    isConnected,
    error,
    subscribe,
    unsubscribe,
    connect,
    disconnect,
  };
}
